import { z } from "zod";
import { offsetDateTimeWithOptionalSecondsSchema } from "./primitives.js";
import { sessionSearchMatchKindSchema } from "./sessions.js";
// Agent-written range bounds; see offsetDateTimeWithOptionalSecondsSchema.
const offsetDateTimeSchema = offsetDateTimeWithOptionalSecondsSchema;
export const episodicSearchModeSchema = z.enum([
    "auto",
    "exact",
    "semantic",
    "hybrid",
]);
export const episodicMatchKindSchema = z.enum([
    "title",
    "user",
    "assistant",
    "both",
]);
export const workerSessionHistoryRequestSchema = z
    .object({
    runId: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    query: z.string().trim().max(500).default(""),
    matchKind: sessionSearchMatchKindSchema,
    actor: z.literal("current").default("current"),
    from: offsetDateTimeSchema.optional(),
    to: offsetDateTimeSchema.optional(),
    channelId: z.string().trim().min(1).max(200).optional(),
    cursor: z.string().trim().min(1).max(2048).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
})
    .superRefine((value, context) => {
    if ((value.from === undefined) !== (value.to === undefined)) {
        context.addIssue({
            code: "custom",
            path: value.from === undefined ? ["from"] : ["to"],
            message: "from and to must be supplied together",
        });
        return;
    }
    if (value.from !== undefined &&
        Date.parse(value.from) >= Date.parse(value.to)) {
        context.addIssue({
            code: "custom",
            path: ["to"],
            message: "to must be later than from",
        });
    }
});
export const sessionHistoryMatchSchema = z.object({
    sessionId: z.string().trim().min(1),
    title: z.string().trim().min(1).max(500),
    snippet: z.string().max(500),
    matchKind: sessionSearchMatchKindSchema,
    matchedAt: z.number().finite(),
    sessionUrl: z.string().url(),
});
export const workerSessionHistoryResponseSchema = z.object({
    matches: z.array(sessionHistoryMatchSchema),
    nextCursor: z.string().nullable(),
});
export const workerEpisodicMemoryRequestSchema = z
    .object({
    runId: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    query: z.string().trim().max(500).default(""),
    mode: episodicSearchModeSchema.default("auto"),
    matchKind: episodicMatchKindSchema.default("both"),
    actor: z.literal("current").default("current"),
    from: offsetDateTimeSchema.optional(),
    to: offsetDateTimeSchema.optional(),
    channelId: z.string().trim().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    minimumScore: z.coerce.number().min(0).max(1).optional(),
    resultPurpose: z.enum(["search_results", "model_context"]).default("search_results"),
    includeRelatedTurns: z.boolean().default(false),
})
    .superRefine((value, context) => {
    if ((value.from === undefined) !== (value.to === undefined)) {
        context.addIssue({
            code: "custom",
            path: value.from === undefined ? ["from"] : ["to"],
            message: "from and to must be supplied together",
        });
    }
    if (value.from && Date.parse(value.from) >= Date.parse(value.to)) {
        context.addIssue({
            code: "custom",
            path: ["to"],
            message: "to must be later than from",
        });
    }
    if ((value.mode === "semantic" || value.mode === "hybrid") &&
        (!value.query || value.matchKind === "title")) {
        context.addIssue({
            code: "custom",
            path: ["query"],
            message: "semantic and hybrid search require a query and user, assistant, or both matching",
        });
    }
    if (value.minimumScore !== undefined && value.mode !== "semantic") {
        context.addIssue({
            code: "custom",
            path: ["minimumScore"],
            message: "minimumScore is only supported for semantic search",
        });
    }
    if (value.resultPurpose === "model_context" &&
        (value.mode !== "semantic" ||
            value.minimumScore === undefined ||
            value.limit > 20)) {
        context.addIssue({
            code: "custom",
            path: ["resultPurpose"],
            message: "model_context results require semantic search, minimumScore, and limit at most 20",
        });
    }
});
export const episodicMemoryMatchSchema = sessionHistoryMatchSchema.extend({
    segmentId: z.string().trim().min(1).nullable(),
    sourceId: z.string().trim().min(1).nullable(),
    // Optional during rolling deployment; current control planes always emit
    // the creation time of the task/session that owns the matched segment.
    sourceTaskCreatedAt: z.number().finite().optional(),
    score: z.number().finite(),
    exactRank: z.number().int().positive().nullable(),
    semanticRank: z.number().int().positive().nullable(),
    // Optional while control-plane and Electric runtime versions converge.
    // A ranked semantic response from the new control plane always supplies it.
    temporalRank: z.number().int().positive().nullable().optional(),
});
export const workerEpisodicMemoryResponseSchema = z.object({
    // Optional only for rolling compatibility; current control planes always
    // return this stable correlation id and memory.trace uses it as operationId.
    retrievalId: z.string().trim().min(1).optional(),
    matches: z.array(episodicMemoryMatchSchema),
    relatedTurns: z.array(sessionHistoryMatchSchema).optional(),
    requestedMode: episodicSearchModeSchema,
    retrievalMode: z.enum(["exact", "semantic", "hybrid", "exact_fallback"]),
    indexGenerationId: z.string().nullable(),
    indexSourceWatermark: z.number().finite().nullable(),
});
